import React, { Fragment, useContext, useEffect, useMemo } from "react";
import { DashboardContext } from "./";
import { getOrdersForFinance } from "./Action";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
} from "chart.js";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Title
);

const Customize = () => {
  const { data, dispatch } = useContext(DashboardContext);
  const { financeOrders, loading } = data;

  useEffect(() => {
    getOrdersForFinance(dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => {
    const orders = Array.isArray(financeOrders) ? financeOrders : [];
    const deliveredOrders = orders.filter((o) => o.status === "Livrée");
    const validOrders = orders.filter((o) => o.status !== "Annulée");
    const cancelledOrders = orders.filter((o) => o.status === "Annulée");

    const totalOrders = orders.length;
    const totalAmount = validOrders.reduce((acc, o) => acc + (o.amount || 0), 0);
    const totalDelivery = validOrders.reduce((acc, o) => acc + (o.deliveryPrice || 0), 0);
    const totalRevenue = deliveredOrders.reduce((acc, o) => acc + (o.amount || 0), 0);
    const totalCancelledAmount = cancelledOrders.reduce((acc, o) => acc + (o.amount || 0), 0);

    const delivered = deliveredOrders.length;
    const pending = orders.filter((o) => ["Nouvelle", "En cours"].includes(o.status)).length;
    const cancelled = cancelledOrders.length;

    return {
      totalOrders,
      totalAmount,
      totalDelivery,
      totalRevenue,
      totalCancelledAmount,
      delivered,
      pending,
      cancelled,
    };
  }, [financeOrders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <img className="w-12 h-12 animate-spin" src="/images/favicon.png" alt="loading" />
      </div>
    );
  }

  if (stats.totalOrders === 0) {
    return (
      <div className="p-6 bg-white rounded-xl shadow text-center">
       
      </div>
    );
  }

  // --- Données graphiques ---
  const financeData = {
    labels: ["Chiffre d'affaire", "Revenus Livrés", "Montant Annulé"],
    datasets: [
      {
        label: "Montants (F)",
        data: [stats.totalAmount, stats.totalRevenue, stats.totalCancelledAmount],
        backgroundColor: ["#5D9E8A", "#356BA1", "#D1453B"], // vert, bleu marine, rouge
        borderRadius: 8,
      },
    ],
  };

  const ordersStatusData = {
    labels: ["Livrées", "En attente", "Annulées"],
    datasets: [
      {
        label: "Commandes",
        data: [stats.delivered, stats.pending, stats.cancelled],
        backgroundColor: ["#356BA1", "#D9AA3D", "#D1453B"], // bleu marine, beige, rouge
        borderWidth: 2,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: true, text: "Statistiques Financières (F CFA)" },
    },
  };

  const doughnutOptions = {
    responsive: true,
    cutout: "60%", // réduit la taille du cercle
    plugins: {
      legend: {
        position: "bottom",
        labels: { font: { size: 14 } },
      },
      title: { display: true, text: "Répartition des Commandes" },
    },
  };

  return (
    <Fragment>
      <div className="relative m-4 bg-white p-6 shadow-lg rounded-xl">
        <h1 className="border-b-2 border-yellow-700 mb-6 pb-2 text-2xl font-semibold">
          Vue Financière Globale
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Diagramme des montants */}
          <div className="p-4 bg-gray-50 rounded-lg shadow-sm">
            <Bar data={financeData} options={barOptions} />
          </div>

          {/* Diagramme des statuts de commande */}
          <div className="p-4 bg-gray-50 rounded-lg shadow-sm flex justify-center">
            <div className="w-64 h-64">
              <Doughnut data={ordersStatusData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Customize;
